/**
 * ContentForge — Content Pipeline View (Kanban)
 *
 * Displays keywords in a pipeline with columns for each stage.
 * Supports adding keywords in bulk and changing their stage.
 */

import { html, nothing } from "lit";
import type { CfKeyword, CfKeywordStage } from "../controllers/contentforge.ts";

// ─── Types ──────────────────────────────────────────────────────────────

export type CfCsvSource = {
  id: string;
  filename: string;
  active: boolean;
  productCount: number;
  uploadedAt: number;
};

export type CfContentProps = {
  keywords: CfKeyword[];
  loading: boolean;
  error: string | null;
  csvSources: CfCsvSource[];
  totalProducts: number;
  activeSiteId: string;
  onRefresh: () => void;
  onAddKeywords: (siteId: string, texts: string[], force?: boolean, keywordType?: string) => void;
  onAddReverseKeywords?: (siteId: string, texts: string[], productUrls: string[]) => void;
  onRemoveKeyword: (id: string) => void;
  onUpdateStage: (id: string, stage: CfKeywordStage) => void;
  onRunStrategist?: (keywordId: string) => void;
  onRunSourcer?: (keywordId: string) => void;
  onRunWriter?: (keywordId: string) => void;
  onRepublish?: (keywordId: string) => void;
  onRegenerateImage?: (keywordId: string, customPrompt?: string) => void;
  onUploadCsv?: (filename: string, content: string) => void;
  onToggleCsv?: (id: string, active: boolean) => void;
  onDeleteCsv?: (id: string) => void;
};

// ─── Constants ──────────────────────────────────────────────────────────

const STAGES: { key: CfKeywordStage; label: string; accent: string }[] = [
  { key: "ideas", label: "Ideas", accent: "var(--cf-accent-blue, #60a5fa)" },
  { key: "outline", label: "Outline", accent: "var(--cf-accent-indigo, #818cf8)" },
  { key: "sourcing", label: "Sourcing", accent: "var(--cf-accent-purple, #a78bfa)" },
  { key: "writing", label: "Writing", accent: "var(--cf-accent-yellow, #fbbf24)" },
  { key: "review", label: "Review", accent: "var(--cf-accent-pink, #f472b6)" },
  { key: "scheduled", label: "Scheduled", accent: "var(--cf-accent-teal, #2dd4bf)" },
  { key: "published", label: "Published", accent: "var(--cf-accent-green, #34d399)" },
];

const NEXT_STAGE: Record<CfKeywordStage, CfKeywordStage | null> = {
  ideas: "outline",
  outline: "sourcing",
  sourcing: "writing",
  writing: "review",
  review: "scheduled",
  scheduled: "published",
  published: null,
};

// ─── Helpers ────────────────────────────────────────────────────────────

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) {
    return "just now";
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderCfContent(props: CfContentProps) {
  const {
    keywords,
    loading,
    error,
    activeSiteId,
    onRefresh,
    onAddKeywords,
    onAddReverseKeywords,
    onRemoveKeyword,
    onUpdateStage,
    onRunStrategist,
    onRunSourcer,
    onRunWriter,
    onRepublish,
    onRegenerateImage,
  } = props;

  // Stats
  const total = keywords.length;
  const published = keywords.filter((k) => k.stage === "published").length;
  const inPipeline = total - published;

  return html`
    <div class="cf-content">
      <!-- Header -->
      <div class="cf-tasks__header">
        <div class="cf-tasks__title">
          <h2>Content Pipeline</h2>
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${onRefresh}>
            Refresh
          </button>
        </div>
        ${error ? html`<div class="cf-error">${error}</div>` : nothing}
      </div>

      <!-- Stats Bar -->
      <div class="cf-stats">
        <div class="cf-stats__item">
          <span class="cf-stats__value">${total}</span>
          <span class="cf-stats__label">Total Keywords</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${inPipeline}</span>
          <span class="cf-stats__label">In Pipeline</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${published}</span>
          <span class="cf-stats__label">Published</span>
        </div>
      </div>

      <!-- Bulk Add Keywords -->
      ${renderBulkAddForm(onAddKeywords, activeSiteId, onAddReverseKeywords)}

      <!-- CSV sources moved to Settings → Product Sources -->

      <!-- Loading -->
      ${
        loading
          ? html`
              <div class="cf-loading">Loading keywords…</div>
            `
          : nothing
      }

      <!-- Pipeline Board -->
      <div class="cf-kanban cf-kanban--wide">
        ${STAGES.map((stage) => {
          const stageKeywords = keywords.filter((k) => k.stage === stage.key);
          return html`
            <div class="cf-kanban__column">
              <div class="cf-kanban__column-header">
                <span class="cf-kanban__column-dot" style="background: ${stage.accent}"></span>
                <span class="cf-kanban__column-title">${stage.label}</span>
                <span class="cf-kanban__column-count">${stageKeywords.length}</span>
              </div>
              <div class="cf-kanban__cards">
                ${
                  stageKeywords.length === 0
                    ? html`
                        <div class="cf-kanban__empty">No keywords</div>
                      `
                    : stageKeywords.map((kw) =>
                        renderKeywordCard(
                          kw,
                          onRemoveKeyword,
                          onUpdateStage,
                          onRunStrategist,
                          onRunSourcer,
                          onRunWriter,
                          onRepublish,
                          onRegenerateImage,
                        ),
                      )
                }
              </div>
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

// ─── Bulk Add Form ──────────────────────────────────────────────────────

function renderBulkAddForm(
  onAddKeywords: (siteId: string, texts: string[], force?: boolean, keywordType?: string) => void,
  activeSiteId: string,
  onAddReverseKeywords?: (siteId: string, texts: string[], productUrls: string[]) => void,
) {
  return html`
    <div style="margin-bottom:20px">
      <div class="cf-new-task">
        <textarea
          class="cf-input cf-textarea"
          id="cf-bulk-keywords"
          rows="2"
          placeholder="Paste keywords (comma-separated or one per line)…"
        ></textarea>
        <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
          <button
            class="cf-btn cf-btn--primary"
            @click=${() => {
              const textarea = document.getElementById("cf-bulk-keywords") as HTMLTextAreaElement;
              const forceCheckbox = document.getElementById("cf-force-add") as HTMLInputElement;
              const infoCheckbox = document.getElementById("cf-informational") as HTMLInputElement;
              const reverseCheckbox = document.getElementById("cf-reverse") as HTMLInputElement;
              const raw = textarea?.value ?? "";
              const texts = raw
                .split(/[,\n]/)
                .map((t) => t.trim())
                .filter(Boolean);
              if (texts.length === 0) {
                return;
              }
              // Reverse mode: collect product URLs and call reverse handler
              if (reverseCheckbox?.checked && onAddReverseKeywords) {
                const urls: string[] = [];
                for (let i = 1; i <= 5; i++) {
                  const input = document.getElementById(`cf-reverse-url-${i}`) as HTMLInputElement;
                  if (input?.value?.trim()) {
                    urls.push(input.value.trim());
                  }
                }
                if (urls.length < 3) {
                  alert("Please provide at least 3 product URLs for reverse mode.");
                  return;
                }
                onAddReverseKeywords(activeSiteId, texts, urls);
                textarea.value = "";
                for (let i = 1; i <= 5; i++) {
                  const input = document.getElementById(`cf-reverse-url-${i}`) as HTMLInputElement;
                  if (input) {
                    input.value = "";
                  }
                }
                return;
              }
              const force = forceCheckbox?.checked ?? false;
              const keywordType = infoCheckbox?.checked ? "informational" : undefined;
              onAddKeywords(activeSiteId, texts, force, keywordType);
              textarea.value = "";
            }}
          >
            + Add Keywords
          </button>
          <label style="display:flex;align-items:center;gap:4px;font-size:0.8rem;color:var(--cf-text-muted,#94a3b8);cursor:pointer">
            <input type="checkbox" id="cf-force-add" style="cursor:pointer" />
            Skip dedup check
          </label>
          <label style="display:flex;align-items:center;gap:4px;font-size:0.8rem;color:var(--cf-text-muted,#94a3b8);cursor:pointer" title="Skip product sourcing — pure informational content">
            <input type="checkbox" id="cf-informational" style="cursor:pointer" />
            ℹ️ Informational (no products)
          </label>
        </div>
      </div>
      <div style="margin-top:8px">
        <label style="display:inline-flex;align-items:center;gap:6px;font-size:0.85rem;color:#10b981;cursor:pointer;padding:4px 10px;border-radius:6px;border:1px solid #10b98133;background:#10b98110;user-select:none" title="Reverse — provide product links instead of auto-sourcing">
          <input
            type="checkbox"
            id="cf-reverse"
            style="cursor:pointer;width:16px;height:16px;accent-color:#10b981"
            @change=${(e: Event) => {
              const checked = (e.target as HTMLInputElement).checked;
              const urlsDiv = document.getElementById("cf-reverse-urls");
              if (urlsDiv) {
                urlsDiv.style.display = checked ? "block" : "none";
              }
            }}
          />
          🔄 Reverse (provide product links)
        </label>
      </div>
      <div id="cf-reverse-urls" style="display:none;margin-top:8px;padding:12px;background:var(--cf-bg-card,#1e293b);border-radius:8px;border:1px solid #10b98144">
        <div style="font-size:0.8rem;color:var(--cf-text-muted,#94a3b8);margin-bottom:8px">Product URLs (3 required, 2 optional):</div>
        ${[1, 2, 3, 4, 5].map(
          (i) => html`
            <input
              class="cf-input"
              id="cf-reverse-url-${i}"
              type="url"
              placeholder="${i <= 3 ? `Product URL ${i} (required)` : `Product URL ${i} (optional)`}"
              style="margin-bottom:4px;font-size:0.8rem;width:100%;box-sizing:border-box"
            />
          `,
        )}
      </div>
    </div>
  `;
}

// ─── Keyword Card ───────────────────────────────────────────────────────

function renderKeywordCard(
  kw: CfKeyword,
  onRemove: (id: string) => void,
  onUpdateStage: (id: string, stage: CfKeywordStage) => void,
  onRunStrategist?: (keywordId: string) => void,
  onRunSourcer?: (keywordId: string) => void,
  onRunWriter?: (keywordId: string) => void,
  onRepublish?: (keywordId: string) => void,
  onRegenerateImage?: (keywordId: string, customPrompt?: string) => void,
) {
  const nextStage = NEXT_STAGE[kw.stage];
  const showStrategist = kw.stage === "ideas" && onRunStrategist && !kw.assignedAgent;
  const isInfoKeyword = (kw as Record<string, unknown>).keywordType === "informational";
  const showSourcer = kw.stage === "outline" && onRunSourcer && !kw.assignedAgent && !isInfoKeyword;
  const showSkipToWrite =
    kw.stage === "outline" && isInfoKeyword && onRunWriter && !kw.assignedAgent;
  const showWriter = kw.stage === "sourcing" && onRunWriter && !kw.assignedAgent;
  const showRepublish = (kw.stage === "published" || kw.stage === "scheduled") && onRepublish;
  return html`
    <div class="cf-kanban__card">
      <div class="cf-kanban__card-title">
        ${kw.text}
        ${
          kw.amazonTagged
            ? html`<span
              style="display: inline-block; background: #FF9900; color: #000; font-size: 10px; font-weight: 700; padding: 1px 5px; border-radius: 3px; margin-left: 6px; vertical-align: middle;"
              title="${kw.amazonProductTitle || kw.amazonAsin || "Amazon product"}"
            >🛒</span>`
            : nothing
        }
        ${
          (kw as Record<string, unknown>).keywordType === "informational"
            ? html`
                <span
                  style="
                    display: inline-block;
                    background: #3b82f6;
                    color: #fff;
                    font-size: 10px;
                    font-weight: 700;
                    padding: 1px 5px;
                    border-radius: 3px;
                    margin-left: 6px;
                    vertical-align: middle;
                  "
                  title="Informational — no products"
                  >ℹ️</span
                >
              `
            : nothing
        }
        ${
          (kw as Record<string, unknown>).reverse
            ? html`
                <span
                  style="
                    display: inline-block;
                    background: #10b981;
                    color: #fff;
                    font-size: 10px;
                    font-weight: 700;
                    padding: 1px 5px;
                    border-radius: 3px;
                    margin-left: 6px;
                    vertical-align: middle;
                  "
                  title="Reverse — manual product links"
                  >🔄</span
                >
              `
            : nothing
        }
      </div>
      <div class="cf-kanban__card-meta">
        ${
          kw.assignedAgent
            ? html`<span class="cf-kanban__card-agent">${kw.assignedAgent}</span>`
            : nothing
        }
        <span class="cf-kanban__card-time">${formatRelativeTime(kw.updatedAt)}</span>
      </div>
      ${
        kw.lastActivity
          ? html`
              <div class="cf-kanban__card-activity" title="${kw.lastActivity.details}">
                <span
                  class="cf-kanban__activity-dot"
                  style="background: ${kw.lastActivity.status === "success" ? "#34d399" : kw.lastActivity.status === "warning" ? "#fbbf24" : kw.lastActivity.status === "error" ? "#f87171" : "#94a3b8"}"
                ></span>
                <span class="cf-kanban__activity-text"
                  >${kw.lastActivity.details.slice(0, 60)}${kw.lastActivity.details.length > 60 ? "…" : ""}</span
                >
              </div>
            `
          : nothing
      }
      <div class="cf-kanban__card-actions">
        ${
          showStrategist
            ? html`
              <button
                class="cf-btn cf-btn--xs cf-btn--primary"
                title="Generate SEO outline"
                @click=${() => onRunStrategist(kw.id)}
              >
                ▶ Outline
              </button>
            `
            : nothing
        }
        ${
          showSourcer
            ? html`
              <button
                class="cf-btn cf-btn--xs cf-btn--primary"
                title="Find matching products"
                @click=${() => onRunSourcer(kw.id)}
              >
                ▶ Source
              </button>
            `
            : nothing
        }
        ${
          showSkipToWrite
            ? html`
              <button
                class="cf-btn cf-btn--xs cf-btn--primary"
                title="Skip sourcing — write informational article"
                @click=${() => {
                  // Advance to sourcing stage (skips Sourcer), then trigger Writer
                  onUpdateStage(kw.id, "sourcing");
                  setTimeout(() => onRunWriter(kw.id), 500);
                }}
              >
                ✍ Write
              </button>
            `
            : nothing
        }
        ${
          showWriter
            ? html`
              <button
                class="cf-btn cf-btn--xs cf-btn--primary"
                title="Generate full article"
                @click=${() => onRunWriter(kw.id)}
              >
                ✍ Write
              </button>
            `
            : nothing
        }
        ${
          showRepublish
            ? html`
              <button
                class="cf-btn cf-btn--xs cf-btn--primary"
                title="Republish — retry static HTML publish"
                @click=${() => onRepublish(kw.id)}
              >
                ↻ Republish
              </button>
            `
            : nothing
        }
        ${
          kw.stage === "scheduled" && onRegenerateImage
            ? html`
              <button
                class="cf-btn cf-btn--xs cf-btn--ghost"
                title="Regenerate featured image"
                @click=${(e: Event) => {
                  const btn = e.currentTarget as HTMLElement;
                  const promptDiv = btn.nextElementSibling as HTMLElement | null;
                  if (promptDiv) {
                    promptDiv.style.display = promptDiv.style.display === "none" ? "flex" : "none";
                  }
                }}
              >
                🖼️ Regen Image
              </button>
              <div style="display:none;gap:4px;align-items:center;margin-top:4px;width:100%">
                <input
                  class="cf-input"
                  id="cf-regen-prompt-${kw.id}"
                  placeholder="Custom prompt (optional)"
                  style="font-size:0.75rem;flex:1;min-width:0"
                />
                <button
                  class="cf-btn cf-btn--xs cf-btn--primary"
                  @click=${() => {
                    const input = document.getElementById(
                      `cf-regen-prompt-${kw.id}`,
                    ) as HTMLInputElement;
                    const customPrompt = input?.value?.trim() || undefined;
                    onRegenerateImage(kw.id, customPrompt);
                  }}
                >
                  Generate
                </button>
              </div>
            `
            : nothing
        }
        ${
          nextStage && !showStrategist && !showSourcer && !showWriter && !showRepublish
            ? html`
              <button
                class="cf-btn cf-btn--xs cf-btn--ghost"
                title="Move to ${nextStage}"
                @click=${() => onUpdateStage(kw.id, nextStage)}
              >
                → ${nextStage}
              </button>
            `
            : nothing
        }
        <button
          class="cf-btn cf-btn--xs cf-btn--danger"
          title="Remove"
          @click=${() => onRemove(kw.id)}
        >
          ✕
        </button>
      </div>
    </div>
  `;
}
